import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Building,
  Package,
  Receipt,
  DollarSign,
  AlertCircle,
  CheckCircle,
  Clock,
  CreditCard,
  RefreshCw,
  Loader,
  FileText,
  Calendar,
  HelpCircle,
  Send,
  Ship,
  Wallet,
} from 'lucide-react';
import { useWarehouseDashboard } from '../hooks/useWarehouseDashboard';
import { useUnknownPackageClaims } from '../hooks/useUnknownPackageClaims';
import ClaimPackageModal from '../components/ClaimPackageModal';
import { useSelector } from 'react-redux';
import { getCourier, getCurrency } from '@state/courierSlice';
import { getUser } from '@state/authSlice';
import { formatCurrency } from '../utils/currencyUtils';
import type { CourierPackage } from '@shared/sdk-v1';

export const WarehouseDashboardRoute = '/warehouse';
export const WarehouseDashboardPageName = 'Warehouse';

const claimStatusColors: Record<string, string> = {
  pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
  approved: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  rejected: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
};

const manifestStatusColors: Record<string, string> = {
  preparing: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
  in_transit: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  received: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  completed: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  processing: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
};

const WarehouseDashboard: React.FC = () => {
  const courier = useSelector(getCourier);
  const user = useSelector(getUser);
  const currency = useSelector(getCurrency);
  const {
    partnerLinks,
    recentInvoices,
    unpaidSummary,
    totalPackages,
    totalManifests,
    recentManifests,
    isLoading,
    error,
    refresh,
  } = useWarehouseDashboard();

  const {
    unknownPackages,
    myClaims,
    submitClaim,
    refresh: refreshClaims,
  } = useUnknownPackageClaims(partnerLinks);

  const [claimModalPkg, setClaimModalPkg] = useState<CourierPackage | null>(null);
  const [claimModalWarehouseId, setClaimModalWarehouseId] = useState<number>(0);

  const handleOpenClaimModal = (pkg: CourierPackage) => {
    setClaimModalPkg(pkg);
    setClaimModalWarehouseId(pkg.courierId || 0);
  };

  const handleCloseClaimModal = () => {
    setClaimModalPkg(null);
    setClaimModalWarehouseId(0);
  };

  const hasClaimedPackage = (packageId?: number) => {
    if (!packageId) return false;
    return myClaims.some(
      (c) => c.packageId === packageId && (c.status === 'pending' || c.status === 'approved')
    );
  };

  const getInvoiceStatusBadge = (status?: string) => {
    const base = 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium';
    switch (status?.toLowerCase()) {
      case 'paid':
        return `${base} bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200`;
      case 'sent':
        return `${base} bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200`;
      case 'pending':
        return `${base} bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200`;
      case 'overdue':
        return `${base} bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200`;
      default:
        return `${base} bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300`;
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col justify-center items-center h-96 space-y-4">
        <Loader className="animate-spin h-8 w-8 text-blue-600" />
        <p className="text-gray-600 dark:text-gray-400">Loading dashboard...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6 text-center">
          <AlertCircle className="h-8 w-8 text-red-500 mx-auto mb-2" />
          <p className="text-red-700 dark:text-red-300">{error}</p>
          <button
            onClick={refresh}
            className="mt-4 px-4 py-2 bg-red-100 dark:bg-red-800 text-red-700 dark:text-red-300 rounded-md hover:bg-red-200 dark:hover:bg-red-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const activePartners = partnerLinks.filter((pl) => pl.status === 'active');
  const hasPartnership = activePartners.length > 0;
  const displayName = user?.businessName || user?.firstName || 'Courier';
  const accountCode = user?.prefix ? user.prefix.toUpperCase() : null;

  return (
    <div className="p-4 space-y-6 pb-24 md:pb-6">
      {/* Welcome Header */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500 dark:text-gray-400">Welcome back</p>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{displayName}</h1>
          <div className="flex items-center gap-2 mt-1">
            {user?.isCourier && (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                Courier
              </span>
            )}
            {accountCode && (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300">
                ACCOUNT {accountCode}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {unpaidSummary?.hasUnpaid && (
            <Link
              to="/invoices"
              className="inline-flex items-center px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 transition-colors"
            >
              <Wallet className="w-4 h-4 mr-2" />
              Pay Balance
            </Link>
          )}
          <button
            onClick={refresh}
            className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600
                     text-sm font-medium rounded-md text-gray-700 dark:text-gray-300
                     bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center gap-2 mb-2">
            <DollarSign className="h-5 w-5 text-orange-500" />
            <p className="text-xs text-gray-500 dark:text-gray-400 uppercase font-medium">Outstanding</p>
          </div>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">
            {formatCurrency(unpaidSummary?.totalUnpaid || 0, currency)}
          </p>
          {unpaidSummary?.overdueCount ? (
            <p className="text-xs text-red-500 mt-1">{unpaidSummary.overdueCount} overdue</p>
          ) : (
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
              {unpaidSummary?.invoiceCount || 0} unpaid
            </p>
          )}
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center gap-2 mb-2">
            <Ship className="h-5 w-5 text-blue-500" />
            <p className="text-xs text-gray-500 dark:text-gray-400 uppercase font-medium">Shipments</p>
          </div>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">{totalManifests}</p>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Total shipments</p>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center gap-2 mb-2">
            <Receipt className="h-5 w-5 text-amber-500" />
            <p className="text-xs text-gray-500 dark:text-gray-400 uppercase font-medium">Invoices</p>
          </div>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">{unpaidSummary?.invoiceCount || 0}</p>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Unpaid invoices</p>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center gap-2 mb-2">
            <Package className="h-5 w-5 text-green-500" />
            <p className="text-xs text-gray-500 dark:text-gray-400 uppercase font-medium">Total Packages</p>
          </div>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">{totalPackages}</p>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Received to date</p>
        </div>
      </div>

      {/* Unpaid Balance Alert */}
      {unpaidSummary?.hasUnpaid && (
        <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <DollarSign className="h-6 w-6 text-orange-500 mr-3" />
              <div>
                <p className="font-semibold text-orange-800 dark:text-orange-200">
                  Outstanding Balance
                </p>
                <p className="text-sm text-orange-600 dark:text-orange-300">
                  {unpaidSummary.invoiceCount} unpaid invoice{unpaidSummary.invoiceCount !== 1 ? 's' : ''}
                  {unpaidSummary.overdueCount > 0 && (
                    <span className="text-red-600 dark:text-red-400 font-medium">
                      {' '}({unpaidSummary.overdueCount} overdue)
                    </span>
                  )}
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-orange-800 dark:text-orange-200">
                {formatCurrency(unpaidSummary.totalUnpaid, currency)}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Recent Shipments */}
      {recentManifests.length > 0 && (
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <div className="flex items-center">
              <Ship className="h-5 w-5 text-gray-500 dark:text-gray-400 mr-2" />
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Recent Shipments
              </h2>
            </div>
            <Link to="/packages" className="text-sm text-primary hover:underline">
              View all
            </Link>
          </div>

          {/* Desktop Table */}
          <div className="hidden sm:block overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Shipment</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Packages</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Weight</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Date</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {recentManifests.map((manifest) => (
                  <tr key={manifest.id} className="hover:bg-gray-50 dark:hover:bg-gray-750">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        {manifest.awbReference || manifest.masterAwb || `#${manifest.id}`}
                      </p>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${
                        manifestStatusColors[manifest.status?.toLowerCase() || ''] || 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                      }`}>
                        {manifest.status?.replace(/_/g, ' ') || 'Unknown'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                      {manifest.userPackageCount}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                      {manifest.userTotalWeight.toFixed(1)} lbs
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {manifest.startDate
                        ? new Date(manifest.startDate).toLocaleDateString()
                        : manifest.dateReceived
                          ? new Date(manifest.dateReceived).toLocaleDateString()
                          : '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <Link
                        to={`/packages?manifest=${manifest.id}`}
                        className="text-sm text-primary hover:underline"
                      >
                        View
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="sm:hidden divide-y divide-gray-200 dark:divide-gray-700">
            {recentManifests.map((manifest) => (
              <Link
                key={manifest.id}
                to={`/packages?manifest=${manifest.id}`}
                className="block p-4 hover:bg-gray-50 dark:hover:bg-gray-750"
              >
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {manifest.awbReference || manifest.masterAwb || `#${manifest.id}`}
                  </p>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium capitalize ${
                    manifestStatusColors[manifest.status?.toLowerCase() || ''] || 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                  }`}>
                    {manifest.status?.replace(/_/g, ' ') || 'Unknown'}
                  </span>
                </div>
                <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
                  <span>{manifest.userPackageCount} packages</span>
                  <span>{manifest.userTotalWeight.toFixed(1)} lbs</span>
                  <span>
                    {manifest.startDate
                      ? new Date(manifest.startDate).toLocaleDateString()
                      : '—'}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Partner Status (only show if no partnership) */}
      {!hasPartnership && (
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center mb-4">
            <Building className="h-5 w-5 text-gray-500 dark:text-gray-400 mr-2" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Partnership Status</h2>
          </div>
          <div className="text-center py-4">
            <Building className="mx-auto h-10 w-10 text-gray-400 mb-2" />
            <p className="text-gray-500 dark:text-gray-400">No active warehouse partnership found.</p>
            <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
              Contact your courier to set up a warehouse partnership.
            </p>
          </div>
        </div>
      )}

      {/* Unknown Packages Section */}
      {unknownPackages.length > 0 && (
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <HelpCircle className="h-5 w-5 text-amber-500 mr-2" />
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Unknown Packages
                </h2>
                <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200">
                  {unknownPackages.length}
                </span>
              </div>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              These packages could not be identified by the warehouse. If any belong to you, submit a claim with proof.
            </p>
          </div>

          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {unknownPackages.map((pkg) => {
              const claimed = hasClaimedPackage(pkg.id);
              return (
                <div key={pkg.id} className="px-6 py-4">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="font-mono text-sm font-medium text-gray-900 dark:text-white">
                        {pkg.trackingNo}
                      </p>
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-500 dark:text-gray-400 mt-1">
                        {pkg.description && <span>{pkg.description}</span>}
                        {pkg.weight && <span>{pkg.weight} lbs</span>}
                        {pkg.merchant && <span>{pkg.merchant}</span>}
                      </div>
                    </div>
                    <div>
                      {claimed ? (
                        <span className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400">
                          <CheckCircle className="w-3.5 h-3.5 mr-1" />
                          Claimed
                        </span>
                      ) : (
                        <button
                          onClick={() => handleOpenClaimModal(pkg)}
                          className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md
                                   text-white bg-blue-600 hover:bg-blue-700"
                        >
                          <Send className="w-3.5 h-3.5 mr-1" />
                          Claim
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* My Claims Section */}
      {myClaims.length > 0 && (
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center">
              <FileText className="h-5 w-5 text-gray-500 dark:text-gray-400 mr-2" />
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                My Claims
              </h2>
            </div>
          </div>
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {myClaims.map((claim) => (
              <div key={claim.id} className="px-6 py-3 flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white font-mono">
                    {claim.package?.trackingNo || `Package #${claim.packageId}`}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Submitted {claim.createdAt ? new Date(claim.createdAt).toLocaleDateString() : ''}
                    {claim.notes && <span className="ml-2">- {claim.notes}</span>}
                  </p>
                </div>
                <span
                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${
                    claimStatusColors[claim.status || 'pending'] || claimStatusColors.pending
                  }`}
                >
                  {claim.status || 'pending'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent Invoices */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div className="flex items-center">
            <Receipt className="h-5 w-5 text-gray-500 dark:text-gray-400 mr-2" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Recent Invoices
            </h2>
          </div>
          <Link
            to="/invoices"
            className="text-sm text-primary hover:underline"
          >
            View All
          </Link>
        </div>

        {recentInvoices.length === 0 ? (
          <div className="text-center py-8">
            <FileText className="mx-auto h-10 w-10 text-gray-400 mb-2" />
            <p className="text-gray-500 dark:text-gray-400">No invoices yet.</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {recentInvoices.map((invoice) => (
              <div key={invoice.id} className="p-4 sm:p-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-gray-900 dark:text-white">
                      Invoice #{invoice.invoiceNumber || 'N/A'}
                    </h3>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-500 dark:text-gray-400 mt-1">
                      <span className="flex items-center">
                        <Package className="w-3.5 h-3.5 mr-1" />
                        {invoice.packageCount || 0} packages
                      </span>
                      {invoice.totalWeight && (
                        <span>{invoice.totalWeight.toFixed(1)} lbs</span>
                      )}
                      {invoice.manifest?.manifestNumber && (
                        <span>Manifest: {invoice.manifest.manifestNumber}</span>
                      )}
                      {invoice.dueDate && (
                        <span className="flex items-center">
                          <Calendar className="w-3.5 h-3.5 mr-1" />
                          Due {new Date(invoice.dueDate).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-lg font-semibold text-gray-900 dark:text-white">
                        {formatCurrency(invoice.grandTotal || 0, currency)}
                      </p>
                      <span className={getInvoiceStatusBadge(invoice.status)}>
                        {invoice.status
                          ? invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)
                          : 'Unknown'}
                      </span>
                    </div>

                    <div className="flex flex-col gap-2">
                      <Link
                        to={`/invoices/${invoice.id}`}
                        className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md
                                 text-blue-700 dark:text-blue-400 bg-blue-100 dark:bg-blue-900
                                 hover:bg-blue-200 dark:hover:bg-blue-800"
                      >
                        <FileText className="w-3.5 h-3.5 mr-1" />
                        Details
                      </Link>
                      {invoice.paymentLink && invoice.status !== 'paid' && (
                        <a
                          href={invoice.paymentLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md
                                   text-white bg-green-500 hover:bg-green-600"
                        >
                          <CreditCard className="w-3.5 h-3.5 mr-1" />
                          Pay
                        </a>
                      )}
                    </div>
                  </div>
                </div>

                {/* Fee Breakdown Summary */}
                {(invoice.warehousingTotal || invoice.freightTotal || invoice.customsTotal || invoice.inspectionTotal) && (
                  <div className="mt-3 flex flex-wrap gap-3 text-xs text-gray-500 dark:text-gray-400">
                    {invoice.warehousingTotal !== undefined && invoice.warehousingTotal > 0 && (
                      <span className="bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">
                        Warehousing: {formatCurrency(invoice.warehousingTotal, currency)}
                      </span>
                    )}
                    {invoice.freightTotal !== undefined && invoice.freightTotal > 0 && (
                      <span className="bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">
                        Freight: {formatCurrency(invoice.freightTotal, currency)}
                      </span>
                    )}
                    {invoice.customsTotal !== undefined && invoice.customsTotal > 0 && (
                      <span className="bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">
                        Handling: {formatCurrency(invoice.customsTotal, currency)}
                      </span>
                    )}
                    {invoice.inspectionTotal !== undefined && invoice.inspectionTotal > 0 && (
                      <span className="bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">
                        Inspection: {formatCurrency(invoice.inspectionTotal, currency)}
                      </span>
                    )}
                    {invoice.tax !== undefined && invoice.tax > 0 && (
                      <span className="bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">
                        Tax: {formatCurrency(invoice.tax, currency)}
                      </span>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Unpaid Invoices Breakdown */}
      {unpaidSummary?.hasUnpaid && unpaidSummary.invoices.length > 0 && (
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center">
              <AlertCircle className="h-5 w-5 text-orange-500 mr-2" />
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Unpaid Invoices
              </h2>
            </div>
          </div>
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {unpaidSummary.invoices.map((inv) => (
              <div key={inv.id} className="px-6 py-3 flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    Invoice #{inv.invoiceNumber}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Due: {new Date(inv.dueDate).toLocaleDateString()}
                    {inv.status === 'overdue' && (
                      <span className="text-red-500 ml-2 font-medium">Overdue</span>
                    )}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-semibold text-gray-900 dark:text-white">
                    {formatCurrency(inv.totalAmount, currency)}
                  </span>
                  <Link
                    to={`/invoices/${inv.id}`}
                    className="text-sm text-primary hover:underline"
                  >
                    View
                  </Link>
                </div>
              </div>
            ))}
          </div>
          <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 rounded-b-lg">
            <div className="flex items-center justify-between">
              <span className="font-semibold text-gray-900 dark:text-white">Total Outstanding</span>
              <span className="text-xl font-bold text-orange-600 dark:text-orange-400">
                {formatCurrency(unpaidSummary.totalUnpaid, currency)}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Claim Modal */}
      {claimModalPkg && (
        <ClaimPackageModal
          isOpen={!!claimModalPkg}
          onClose={handleCloseClaimModal}
          pkg={claimModalPkg}
          warehouseCourierId={claimModalWarehouseId}
          onSubmit={submitClaim}
        />
      )}
    </div>
  );
};

export default WarehouseDashboard;
